import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Home } from 'lucide-react';
import NotificationSettings from '../components/NotificationSettings';
import EmailNotificationSettings from '../components/EmailNotificationSettings';
import PushNotificationSettings from '../components/PushNotificationSettings';
import Footer from '../components/Footer';
import { useSafeBack } from '../hooks/useSafeBack';

/**
 * Notification Settings Page
 * 
 * Allows users to manage their SMS notification preferences.
 * In-app notifications are always enabled and cannot be toggled.
 */
const NotificationSettingsPage = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const { goBack } = useSafeBack('/dashboard');

  if (!token) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header
        className="sticky top-0 z-30 bg-[#FFFFFF] border-b border-[#E4E4E7]"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <div className="mx-auto max-w-2xl px-4 pb-4 flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={goBack}
            className="h-10 w-10 rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="h-8 w-8 rounded-full"
            data-testid="nav-home-btn"
            aria-label="Dashboard"
          >
            <Home className="w-4 h-4 text-zinc-400" />
          </Button>
          <h1 className="text-xl font-bold text-zinc-900 ml-1">Notification Settings</h1>
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-4 py-6 space-y-6">
        <PushNotificationSettings />
        <Button
          variant="ghost"
          onClick={() => navigate('/notification-settings/how-it-works')}
          className="h-auto w-full justify-between gap-3 whitespace-normal rounded-lg px-3 py-2 text-left focus-visible:ring-2 focus-visible:ring-orange-500"
          data-testid="notification-transparency-link"
        >
          <span className="flex min-w-0 flex-col items-start">
            <span className="text-sm font-medium text-zinc-700">Wondering what gets sent when?</span>
            <span className="text-sm font-semibold text-orange-700">Learn how notifications work</span>
          </span>
          <ArrowRight className="h-4 w-4 shrink-0 text-orange-600" aria-hidden="true" />
        </Button>
        <EmailNotificationSettings />
        <NotificationSettings />
      </div>

      <Footer variant="slim" />
    </div>
  );
};

export default NotificationSettingsPage;
